import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  LinearProgress,
  Switch,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  handleSpringDataMutationRequest,
  handleSpringDataRequest,
} from "../../utils/apiUtils";

const DAY_OPTIONS = [7, 30, 90];
const CUSTOM_RANGE = "custom";

function todayLocal() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoLocal(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function fmtWhen(raw) {
  if (!raw) return "—";
  // Backend already converts created_at to hotel-local wall-clock time (CONVERT_TZ), so this
  // is a plain "yyyy-MM-dd HH:mm:ss[.f]" string to display as-is, not a UTC/offset-bearing value.
  return String(raw).replace("T", " ").replace(/\.\d+$/, "");
}

function parseSpringMessage(data) {
  if (data == null) return {};
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  if (typeof data !== "object") return {};
  if ("message" in data) {
    const msg = data.message;
    if (typeof msg === "string") {
      try {
        return JSON.parse(msg);
      } catch {
        return {};
      }
    }
    if (msg && typeof msg === "object") return msg;
  }
  return data;
}

function actorEmail(raw) {
  const t = raw != null ? String(raw).trim() : "";
  return t || "system";
}

// Free and monthly credits are part of the plan ("Included"); purchased credits are a paid top-up ("Additional").
function usageType(row) {
  return (row.debited_from_purchased ?? 0) > 0 ? "Additional" : "Included";
}

export default function AiCreditConfig({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creditsTotal, setCreditsTotal] = useState("0");
  const [creditMultiplier, setCreditMultiplier] = useState("");
  const [globalMultiplier, setGlobalMultiplier] = useState("3");
  const [globalCreditUsd, setGlobalCreditUsd] = useState("0.01");
  const [enforcement, setEnforcement] = useState(true);
  const [balance, setBalance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [usageLog, setUsageLog] = useState([]);
  const [hotelTimezone, setHotelTimezone] = useState("");
  const [days, setDays] = useState(30);
  const [customFrom, setCustomFrom] = useState(() => daysAgoLocal(30));
  const [customTo, setCustomTo] = useState(() => todayLocal());
  const [featureFilter, setFeatureFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [customCredits, setCustomCredits] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [granting, setGranting] = useState(false);

  const isCustomRange = days === CUSTOM_RANGE;
  const customRangeValid =
    isCustomRange && !!customFrom && !!customTo && customFrom <= customTo;

  const load = useCallback(async () => {
    if (!hotelId) return;
    if (isCustomRange && !customRangeValid) return;
    setLoading(true);
    try {
      const effectiveDays = isCustomRange ? 90 : days;
      const rangeParams = customRangeValid
        ? `&from=${customFrom}&to=${customTo}`
        : "";
      const [balRes, sumRes, logRes, globalRes, purchasesRes] =
        await Promise.all([
        handleSpringDataRequest(
          `core/api/v1/ai-credit/balance?hotel_id=${hotelId}&hotelId=${hotelId}`
        ),
        handleSpringDataRequest(
          `core/api/v1/ai-credit/usage-summary?hotel_id=${hotelId}&hotelId=${hotelId}&days=${effectiveDays}${rangeParams}`
        ),
        handleSpringDataRequest(
          `core/api/v1/ai-credit/usage-log?hotel_id=${hotelId}&hotelId=${hotelId}&days=${effectiveDays}&limit=100${rangeParams}`
        ),
        handleSpringDataRequest("core/api/v1/ai-credit/global-policy"),
        handleSpringDataRequest(
          `core/api/v1/ai-credit/purchases?hotel_id=${hotelId}&hotelId=${hotelId}&limit=50`
        ),
      ]);
      const bal = parseSpringMessage(balRes);
      const sum = parseSpringMessage(sumRes);
      const logBody = parseSpringMessage(logRes);
      const global = parseSpringMessage(globalRes);
      const purchaseList = parseSpringMessage(purchasesRes);
      setBalance(bal);
      setSummary(sum);
      setPurchases(Array.isArray(purchaseList) ? purchaseList : []);
      setUsageLog(Array.isArray(logBody?.entries) ? logBody.entries : []);
      setHotelTimezone(logBody?.hotel_timezone || "");
      setFeatureFilter("");
      setUserFilter("");
      if (global.default_credit_multiplier != null) {
        setGlobalMultiplier(String(global.default_credit_multiplier));
      }
      if (global.credit_usd_value != null) {
        setGlobalCreditUsd(String(global.credit_usd_value));
      }
      if (bal.monthly_credits_allowance != null) {
        setCreditsTotal(String(bal.monthly_credits_allowance));
      } else if (bal.credits_total != null) {
        setCreditsTotal(String(bal.credits_total));
      }
      if (bal.credit_multiplier != null) {
        setCreditMultiplier(String(bal.credit_multiplier));
      } else {
        setCreditMultiplier("");
      }
      if (bal.enforcement_enabled != null) {
        setEnforcement(!!bal.enforcement_enabled);
      } else {
        setEnforcement(true);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Session expired — please sign in again");
      } else {
        toast.error("Failed to load AI credit data");
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [hotelId, days, isCustomRange, customRangeValid, customFrom, customTo]);

  useEffect(() => {
    load();
  }, [load]);

  const featureOptions = useMemo(() => {
    const set = new Set();
    for (const row of usageLog) {
      if (row.feature) set.add(row.feature);
    }
    return Array.from(set).sort();
  }, [usageLog]);

  const userOptions = useMemo(() => {
    const set = new Set();
    for (const row of usageLog) {
      set.add(actorEmail(row.user_email));
    }
    return Array.from(set).sort();
  }, [usageLog]);

  const filteredUsageLog = useMemo(() => {
    return usageLog.filter((row) => {
      if (featureFilter && row.feature !== featureFilter) return false;
      if (userFilter && actorEmail(row.user_email) !== userFilter) return false;
      return true;
    });
  }, [usageLog, featureFilter, userFilter]);

  const onDaysChange = (value) => {
    if (value === CUSTOM_RANGE) {
      setDays(CUSTOM_RANGE);
    } else {
      setDays(Number(value));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hotel_id: String(hotelId),
        monthly_credits_allowance: parseFloat(creditsTotal) || 0,
        credits_total: parseFloat(creditsTotal) || 0,
        enforcement_enabled: enforcement,
      };
      const mult = creditMultiplier.trim();
      if (mult !== "") {
        payload.credit_multiplier = parseFloat(mult);
      } else {
        payload.credit_multiplier = null;
      }
      await handleSpringDataMutationRequest(
        "POST",
        "core/api/v1/ai-credit/set-limit",
        payload
      );
      toast.success("Hotel AI credit limits saved");
      await load();
    } catch (e) {
      toast.error("Failed to save AI credit limits");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const grantCustomCredits = async () => {
    const credits = parseFloat(customCredits);
    if (!Number.isFinite(credits) || credits <= 0 || credits > 1_000_000) {
      toast.error("Enter credits between 0 and 1,000,000 (exclusive of 0)");
      return;
    }
    setGranting(true);
    try {
      const payload = {
        hotel_id: String(hotelId),
        credits,
      };
      const note = customNote.trim();
      if (note) payload.note = note.slice(0, 100);
      const res = await handleSpringDataMutationRequest(
        "POST",
        "core/api/v1/ai-credit/grant",
        payload
      );
      const body = parseSpringMessage(res);
      toast.success(`Granted ${credits} custom credits (no charge)`);
      if (body?.credits_remaining != null) {
        setBalance((prev) => ({
          ...(prev || {}),
          credits_remaining: body.credits_remaining,
          purchased_credits_remaining: body.purchased_credits_remaining,
        }));
      }
      setCustomCredits("");
      setCustomNote("");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to grant credits"
      );
      console.error(e);
    } finally {
      setGranting(false);
    }
  };

  const customGrants = useMemo(
    () =>
      purchases.filter(
        (row) => row.kind === "custom" || row.pack_id == null
      ),
    [purchases]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const freeRemaining =
    balance?.free_credits_remaining ?? balance?.subscription_credits_remaining ?? 0;
  const monthlyRemaining = balance?.monthly_credits_remaining ?? 0;
  const purchRemaining = balance?.purchased_credits_remaining ?? 0;
  const remaining =
    balance?.credits_remaining ?? freeRemaining + monthlyRemaining + purchRemaining;
  const monthlyAllowance =
    balance?.monthly_credits_allowance ?? balance?.credits_total ?? creditsTotal;
  const effectiveMult =
    balance?.effective_credit_multiplier ?? globalMultiplier;
  const byFeature = summary?.by_feature || [];

  const periodTotalCredits = byFeature.reduce(
    (sum, row) => sum + (row.credits_used || 0),
    0
  );
  const maxFeatureCredits = Math.max(
    1,
    ...byFeature.map((row) => row.credits_used || 0)
  );

  const bucketPct = (rem, allowance, neverExpires) => {
    const hasAllowance = allowance > 0;
    if (hasAllowance) return Math.max(0, Math.min(100, (rem / allowance) * 100));
    return rem > 0 ? 100 : 0;
  };

  return (
    <Box p={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="h6">AI Credits</Typography>
        <Chip
          size="small"
          label={balance?.status || "ACTIVE"}
          color={balance?.status === "ACTIVE" || !balance?.status ? "success" : "default"}
          variant="outlined"
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Three buckets: (1) one-time free, (2) monthly (resets on billing
        anniversary — the day monthly credits were first set — no rollover),
        (3) purchased (never expire). Debit order: free → monthly → purchased.
        1 credit = ${globalCreditUsd || "0.01"} prepaid.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2.5 }}>
          <Box sx={{ flex: 1, minWidth: 160, p: 1.5, borderRadius: 1, bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Credits remaining
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {Number(remaining).toFixed(1)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Free + monthly + purchased
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 160, p: 1.5, borderRadius: 1, bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Used in selected period
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {Number(periodTotalCredits).toFixed(1)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isCustomRange && customRangeValid
                ? `${customFrom} to ${customTo}`
                : `Last ${days} days`}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 160, p: 1.5, borderRadius: 1, bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Multiplier
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {effectiveMult}×
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Enforcement: {enforcement ? "ON" : "OFF"}
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                Free (one-time)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Number(freeRemaining).toFixed(1)} left
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={freeRemaining > 0 ? 100 : 0}
              sx={{ height: 6, borderRadius: 3, bgcolor: "grey.200" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                Monthly allowance
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Number(monthlyRemaining).toFixed(1)} / {Number(monthlyAllowance).toFixed(0)} left
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={bucketPct(monthlyRemaining, monthlyAllowance)}
              sx={{ height: 6, borderRadius: 3, bgcolor: "grey.200" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                Purchased
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Number(purchRemaining).toFixed(1)} left · never expires
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={purchRemaining > 0 ? 100 : 0}
              sx={{ height: 6, borderRadius: 3, bgcolor: "grey.200" }}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
          Cycle: {balance?.cycle_start || "—"} → {balance?.cycle_end || "—"}
        </Typography>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Monthly allowance (hotel)"
            type="number"
            value={creditsTotal}
            onChange={(e) => setCreditsTotal(e.target.value)}
            helperText="Allowance only — remaining refills next anniversary"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Multiplier override"
            type="number"
            inputProps={{ step: "0.1" }}
            value={creditMultiplier}
            onChange={(e) => setCreditMultiplier(e.target.value)}
            placeholder={`Global: ${globalMultiplier}`}
            helperText="Blank = use global"
          />
        </Grid>
        <Grid item xs={12} md={3} display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={enforcement}
                onChange={(e) => setEnforcement(e.target.checked)}
              />
            }
            label="Enforce hard block at zero"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            fullWidth
          >
            {saving ? <CircularProgress size={22} /> : "Save hotel limits"}
          </Button>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" gutterBottom>
        Grant custom credits
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Adds to the purchased pool with no charge (max 1,000,000 per grant).
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "flex-start" }}
        >
          <TextField
            label="Credits"
            type="number"
            size="small"
            value={customCredits}
            onChange={(e) => setCustomCredits(e.target.value)}
            inputProps={{ min: 0.01, max: 1000000, step: "any" }}
            sx={{ width: { xs: "100%", sm: 160 } }}
          />
          <TextField
            label="Note (optional)"
            size="small"
            value={customNote}
            onChange={(e) => setCustomNote(e.target.value.slice(0, 100))}
            helperText={`${customNote.length}/100`}
            fullWidth
          />
          <Button
            variant="contained"
            disabled={granting || !customCredits}
            onClick={grantCustomCredits}
            sx={{ minWidth: 120, whiteSpace: "nowrap" }}
          >
            {granting ? <CircularProgress size={22} /> : "Grant"}
          </Button>
        </Stack>
      </Paper>

      <Typography variant="subtitle1" gutterBottom>
        Custom grants
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Ops grants only — pack buy/grant rows are not listed here.
      </Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>When{hotelTimezone ? ` (${hotelTimezone})` : ""}</TableCell>
            <TableCell align="right">Credits</TableCell>
            <TableCell>Note</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customGrants.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} align="center">
                No custom grants yet
              </TableCell>
            </TableRow>
          )}
          {customGrants.map((row) => {
            const inv = row.invoice_pg_id || "";
            const note =
              inv === "CUSTOM_GRANT"
                ? "—"
                : inv.startsWith("CUSTOM_GRANT:")
                  ? inv.slice("CUSTOM_GRANT:".length)
                  : inv || "—";
            return (
              <TableRow key={row.id}>
                <TableCell>{fmtWhen(row.created_at, hotelTimezone)}</TableCell>
                <TableCell align="right">
                  {Number(row.credits_added).toLocaleString()}
                </TableCell>
                <TableCell>{note}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Typography variant="subtitle1" gutterBottom>
        Usage{" "}
        {isCustomRange && customRangeValid
          ? `${customFrom} to ${customTo}`
          : `last ${days} days`}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Feature</TableCell>
            <TableCell align="right">Calls</TableCell>
            <TableCell align="right">Tokens</TableCell>
            <TableCell align="right">Credits</TableCell>
            <TableCell>Share</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {byFeature.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No usage recorded yet
              </TableCell>
            </TableRow>
          )}
          {byFeature
            .slice()
            .sort((a, b) => (b.credits_used || 0) - (a.credits_used || 0))
            .map((row) => (
              <TableRow key={row.feature}>
                <TableCell>{row.feature}</TableCell>
                <TableCell align="right">{row.call_count}</TableCell>
                <TableCell align="right">{row.total_tokens}</TableCell>
                <TableCell align="right">
                  {Number(row.credits_used).toFixed(2)}
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(
                      2,
                      ((row.credits_used || 0) / maxFeatureCredits) * 100
                    )}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mt: 3, mb: 1 }}
      >
        <Typography variant="subtitle1">
          Per-call usage (
          {isCustomRange && customRangeValid
            ? `${customFrom} to ${customTo}`
            : `last ${days} days`}
          , newest 100)
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            select
            size="small"
            label="Days"
            value={days}
            onChange={(e) => onDaysChange(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 90 }}
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
            <option value={CUSTOM_RANGE}>Custom range</option>
          </TextField>
          {isCustomRange && (
            <>
              <TextField
                size="small"
                label="From"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: customTo }}
              />
              <TextField
                size="small"
                label="To"
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: customFrom, max: todayLocal() }}
              />
              {!customRangeValid && (
                <Typography variant="caption" color="error">
                  Pick a valid range
                </Typography>
              )}
            </>
          )}
          <TextField
            select
            size="small"
            label="Feature"
            value={featureFilter}
            onChange={(e) => setFeatureFilter(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          >
            <option value="">All</option>
            {featureOptions.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="User"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          >
            <option value="">All</option>
            {userOptions.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </TextField>
        </Stack>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When{hotelTimezone ? ` (${hotelTimezone})` : ""}</TableCell>
            <TableCell>Feature</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Model</TableCell>
            <TableCell align="right">Tokens</TableCell>
            <TableCell align="right">Base (USD)</TableCell>
            <TableCell align="right">×</TableCell>
            <TableCell align="right">Credits Charged</TableCell>
            <TableCell align="right">Charged (USD)</TableCell>
            <TableCell>Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUsageLog.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} align="center">
                No per-call usage recorded yet
              </TableCell>
            </TableRow>
          )}
          {filteredUsageLog.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{fmtWhen(row.created_at, hotelTimezone)}</TableCell>
              <TableCell>{row.feature}</TableCell>
              <TableCell>{actorEmail(row.user_email)}</TableCell>
              <TableCell>{row.model || "—"}</TableCell>
              <TableCell align="right">{row.total_tokens}</TableCell>
              <TableCell align="right">
                {row.base_credits != null
                  ? Number(row.base_credits).toFixed(3)
                  : "—"}
              </TableCell>
              <TableCell align="right">
                {row.credit_multiplier != null ? row.credit_multiplier : "—"}
              </TableCell>
              <TableCell align="right">
                {Number(row.credits_charged).toFixed(3)}
              </TableCell>
              <TableCell align="right">
                ${(Number(row.credits_charged) * (parseFloat(globalCreditUsd) || 0.01)).toFixed(3)}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  color={usageType(row) === "Additional" ? "warning" : "default"}
                  label={usageType(row)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
